import { useState, useEffect } from 'react';
import { getSampleDecisionResult } from '../api/decisionEngine';
import type { DecisionEngineResult } from '../api/decisionEngine';
import { SystemTypeCard } from '@system-rec/decision-ui';
import type { SystemTypeCardData } from '@system-rec/decision-ui';
import type { RecommendationOption } from '@core/types';

function SystemsPage() {
  const [result, setResult] = useState<DecisionEngineResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSampleDecisionResult()
      .then(setResult)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="page systems-page">
        <div className="page-header">
          <h2>Recommended Systems</h2>
          <p>Loading system recommendations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page systems-page">
        <div className="page-header">
          <h2>Recommended Systems</h2>
          <p className="error">Error loading data: {error}</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return null;
  }

  const { output } = result;

  // Combine primary and alternatives
  const allRecommendations: RecommendationOption[] = [
    output.primary,
    ...output.alternatives,
  ];

  // Map to SystemTypeCardData format
  const mapToCardData = (rec: RecommendationOption): SystemTypeCardData => {
    const suitability: 'high' | 'medium' | 'low' = 
      rec.confidence === 'high' ? 'high' :
      rec.confidence === 'medium' ? 'medium' : 'low';

    return {
      id: rec.id,
      title: rec.title,
      systemType: rec.systemType,
      suitability,
      reasons: [rec.why, ...rec.pros],
      constraints: rec.cons,
      longevity: rec.longevity?.expectedLifespanYears || { min: 10, max: 15 },
      replacements: rec.longevity?.replacementsInPeriod || 0,
    };
  };

  return (
    <div className="page systems-page">
      <div className="page-header">
        <h2>Recommended Systems</h2>
        <p className="page-subtitle">
          Based on your property and preferences, here are the recommended heating systems
        </p>
      </div>

      <div className="systems-grid">
        {allRecommendations.map((rec) => (
          <SystemTypeCard 
            key={rec.id}
            data={mapToCardData(rec)}
          />
        ))}
      </div>

      {output.assumptions && output.assumptions.length > 0 && (
        <section className="systems-notes">
          <h3>Assumptions & Notes</h3>
          <ul className="notes-list">
            {output.assumptions.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        </section>
      )}

      {output.baselineResult && (
        <section className="comparison-note">
          <div className="info-card">
            <h4>Compare to Baseline</h4>
            <p>
              Your current system has a {output.baselineResult.periodYears}-year total cost of{' '}
              <strong>£{output.baselineResult.totalCost_gbp.toLocaleString()}</strong>.
              The recommended systems show potential savings when compared to continuing with your existing setup.
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

export default SystemsPage;
